import { cloneElement, HTMLAttributes, ReactElement, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography as T,
} from "@mui/material";
import useCohorts from "cohorts/hooks/useCohorts";
import useConfiguredDatasets from "cohorts/hooks/useConfiguredDatasets";
import { DatasetListItem } from "datasets/types/interfaces/datasetListItem";
import { DEFAULT_DATASET_ID } from "datasets/utils/dataset";
import { DatasetIcon } from "~/assets/icons";
import useErrorToast from "~/contexts/error-toast";
import rumClient, { trackEvent } from "~/utils/monitoring";

interface ModalBodyProps {
  onClose: () => void;
  onConfirm: (datasetId: string) => Promise<void>;
  loading: boolean;
}

const ModalBody = ({ onClose, onConfirm, loading }: ModalBodyProps) => {
  const [selectedDatasetId, setSelectedDatasetId] = useState<string>("");
  const { data: configuredDatasets, isLoading: datasetsLoading } = useConfiguredDatasets();

  const handleDatasetChange = (event: SelectChangeEvent<string>) => {
    setSelectedDatasetId(event.target.value);
  };

  const handleConfirm = () => {
    onConfirm(selectedDatasetId);
  };

  return (
    <>
      <DialogContent>
        <FormControl fullWidth margin="normal">
          <T variant="subtitle2" sx={{ mb: 0.5 }}>
            Select the dataset to use for this cohort
          </T>
          <Select
            size="small"
            sx={{ height: 43 }}
            value={selectedDatasetId}
            onChange={handleDatasetChange}
            disabled={datasetsLoading}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) {
                return (
                  <T variant="body2" sx={{ opacity: 0.7 }}>
                    Select a dataset...
                  </T>
                );
              }
              const dataset = configuredDatasets?.find((d: DatasetListItem) => d.id === selected);
              return dataset?.title || "";
            }}
          >
            {configuredDatasets?.map((dataset: DatasetListItem) => (
              <MenuItem key={dataset.id} value={dataset.id}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    component={DatasetIcon}
                    width={16}
                    height={16}
                    minWidth={16}
                    minHeight={16}
                  />
                  {dataset.title}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          disabled={!selectedDatasetId}
          loading={loading}
          onClick={handleConfirm}
        >
          Create
        </LoadingButton>
      </DialogActions>
    </>
  );
};

const CreateCohort = (props: {
  callToAction: ReactElement<HTMLAttributes<HTMLInputElement> & { loading: boolean }>;
}) => {
  const navigate = useNavigate();
  const { mutate } = useCohorts();
  const { showError } = useErrorToast();
  const [creating, setCreating] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const { data: configuredDatasets } = useConfiguredDatasets();

  const createCohort = async (datasetId: string) => {
    setCreating(true);

    try {
      const resp = await fetch("/api/cohort-explorer/cohorts", {
        method: "POST",
        body: JSON.stringify({
          dataset_id: datasetId,
        }),
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!resp.ok) {
        throw Error("There was a problem trying to create cohort");
      }

      mutate();
      const cohort = await resp.json();
      navigate(`/cohorts/${cohort.id}`);

      trackEvent("cohort_created", {
        cohort_id: cohort.id,
        dataset_id: datasetId,
        content: {},
      });
    } catch (e) {
      console.error(e);
      rumClient?.recordError(e);
      showError("Unable to create cohort");
    }

    setCreating(false);
  };

  const handleCreate = async () => {
    if (Number(configuredDatasets?.length) > 1) {
      setModalOpen(true);
    } else {
      await createCohort(configuredDatasets?.[0]?.id || DEFAULT_DATASET_ID);
    }
  };

  const handleConfirm = async (datasetId: string) => {
    setModalOpen(false);
    await createCohort(datasetId);
  };

  const handleClose = () => {
    setModalOpen(false);
  };

  const boundCTA = cloneElement(props.callToAction, {
    loading: creating,
    onClick: () => handleCreate(),
    ["aria-label"]: "Create Cohort",
  });

  if (!configuredDatasets?.length) {
    return null;
  }

  return (
    <Box sx={{ pointerEvents: creating ? "none" : "auto" }}>
      {boundCTA}
      <Dialog fullWidth open={modalOpen} onClose={handleClose}>
        <DialogTitle>New Cohort</DialogTitle>
        <ModalBody onClose={handleClose} onConfirm={handleConfirm} loading={creating} />
      </Dialog>
    </Box>
  );
};

export default CreateCohort;
